import { useMemo } from "react";
import {
  BasicBotData,
  BotData,
  BotStatus,
  BotType,
  GetBotsDataRequest,
  SortingOrder,
  WorkspaceType,
} from "@shared/types";
import {
  getSortedDataBySortSelect,
  sortByNumberProperty,
} from "@shared/utils/general";
import { BotsTab, MyBotsSort } from "@shared/features/bots/types";
import { useSelector } from "react-redux";
import { useFilterBotsBySearch } from "@shared/features/bots/hooks";
import { useIncludeArchived } from "features/filters/hooks";
import { filterBots, sortBotsByAmount } from "@shared/features/bots/utils";
import { sharedLib } from "@shared/services/shared.service";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  BOT_TYPE_TO_FILTERS_SLICE,
  CHANNEL_CONFIGURATIONS_SORTS,
  SIGNALS_BOTS_SORTS,
} from "@shared/features/bots/constants";
import { groupConfigurationsFilters } from "@shared/slices/filters.slice";
import { pickBy } from "lodash";
import { selectFilteredBots } from "@shared/slices/bots.slice";
import { selectWorkspace } from "@shared/slices/common.slice";

interface useMyBotsProps {
  botType: BotType;
  sortBy: MyBotsSort;
  sortDirection: SortingOrder;
  searchValue?: string;
  isChannelConfiguration?: boolean;
  active?: boolean;
}

const useMyBots = ({
  botType,
  sortBy,
  sortDirection,
  searchValue,
  isChannelConfiguration,
  active = true,
}: useMyBotsProps) => {
  const workspace = useSelector(selectWorkspace);
  const isAdminWorkspace = workspace === WorkspaceType.ADMIN;
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;
  const isAdminTradingViewToSignalsBot =
    isAdminWorkspace && isTradingViewSignalsBot;
  const subGroupSelector = useMemo(() => {
    return isChannelConfiguration || isAdminTradingViewToSignalsBot
      ? groupConfigurationsFilters.selectors.selectGroupsFilter
      : () => undefined;
  }, [isChannelConfiguration]);
  const [subGroupId] = useSelector(subGroupSelector) ?? [];
  const includeArchived = useIncludeArchived(botType);
  const getBotsDataArgs = useMemo(() => {
    if (!active || isChannelConfiguration) return skipToken;
    const baseArgs: GetBotsDataRequest = {
      bot_type: botType,
      include_archived: includeArchived,
    };
    if (isAdminTradingViewToSignalsBot) {
      baseArgs.sub_group_id = subGroupId;
    }
    return baseArgs;
  }, [
    active,
    isChannelConfiguration,
    botType,
    isAdminTradingViewToSignalsBot,
    subGroupId,
    includeArchived,
  ]);
  const getBotsDataStatus =
    sharedLib.sharedApi.bots.useGetBotsDataQuery(getBotsDataArgs);
  const getSubGroupBotConfigurationsStatus =
    sharedLib.sharedApi.bots.useGetSubGroupBotConfigurationsQuery(
      isChannelConfiguration && subGroupId
        ? {
            sub_group_id: subGroupId,
            verify_is_admin: true,
          }
        : skipToken
    );
  const isSignalsBot = botType === BotType.SIGNAL;

  const botsData =
    getBotsDataStatus?.data?.data ??
    (getSubGroupBotConfigurationsStatus?.data?.data as
      | BasicBotData[]
      | undefined);

  const filtersSlice = isChannelConfiguration
    ? groupConfigurationsFilters
    : BOT_TYPE_TO_FILTERS_SLICE[botType];
  const filtersData = useSelector(filtersSlice.selectors.selectFiltersData);
  const selector = useMemo(() => {
    return getBotsDataArgs === skipToken
      ? () => undefined
      : selectFilteredBots(getBotsDataArgs);
  }, [getBotsDataArgs]);
  const selectorFilteredBots = useSelector(selector);
  const filteredBots = useMemo(() => {
    if (isChannelConfiguration || isAdminTradingViewToSignalsBot) {
      return filterBots(botsData, filtersData);
    }
    if (isTradingViewSignalsBot)
      return selectorFilteredBots?.filter((bot) => bot.sub_group_is_personal);
    return selectorFilteredBots;
  }, [botsData, filtersData, isChannelConfiguration, selectorFilteredBots]);

  const getSortToPath = () => {
    if (isChannelConfiguration)
      return pickBy(SORT_TO_PATH, (_, key) => {
        return CHANNEL_CONFIGURATIONS_SORTS.has(key as any);
      }) as SortToPath;
    else if (isSignalsBot)
      return pickBy(SORT_TO_PATH, (_, key) => {
        return SIGNALS_BOTS_SORTS.has(key as any);
      }) as SortToPath;
    return pickBy(SORT_TO_PATH, (_, key) => {
      return key !== MyBotsSort.LAST_TRADE_OPENED;
    }) as SortToPath;
  };

  let bots = useMemo(() => {
    return getSortedDataBySortSelect<BasicBotData, MyBotsSort>(
      filteredBots || [],
      sortBy,
      OPPOSITE_SORTS.includes(sortBy) ? -sortDirection : sortDirection,
      getSortToPath(),
      STRING_SORTS,
      ABS_SORTS,
      (a, b) => {
        const statusSort =
          +(b.status === BotStatus.ACTIVE) - +(a.status === BotStatus.ACTIVE);
        const numOpenTradesSort = isSignalsBot
          ? b.number_of_open_trades - a.number_of_open_trades
          : 0;
        const numClosedTradesSort = isSignalsBot
          ? b.number_of_closed_trades - a.number_of_closed_trades
          : 0;
        return statusSort || numOpenTradesSort || numClosedTradesSort;
      },
      {
        [MyBotsSort.OPEN_TRADES]: (a, b) => {
          return sortByNumberProperty(
            a,
            b,
            "open_trades_change[0]",
            sortDirection
          );
        },
      }
    );
  }, [sortBy, sortDirection, filteredBots]);

  bots = useFilterBotsBySearch(bots, searchValue, BotsTab.MY_BOTS) || [];

  return {
    bots,
    isFetching:
      getBotsDataStatus.isFetching ||
      getSubGroupBotConfigurationsStatus.isFetching,
    getBotsDataStatus: isChannelConfiguration
      ? getSubGroupBotConfigurationsStatus
      : getBotsDataStatus,
    unfilteredBots: botsData || [],
    getBotsDataArgs,
  };
};

export default useMyBots;

type SortToPath = {
  [sortKey in MyBotsSort]?:
    | string
    | string[]
    | ((
        a: BotData | BasicBotData,
        b: BotData | BasicBotData,
        sortDirection: SortingOrder
      ) => number);
};
const SORT_TO_PATH: SortToPath = {
  [MyBotsSort.NAME]: "name",
  [MyBotsSort.TOTAL_PROFIT]: "change[0]",
  [MyBotsSort.NEXT_TAKE_PROFIT]: "active_trade.next_take_profit.distance",
  [MyBotsSort.LAST_ENTRY]: "active_trade.last_entry.activity_time",
  [MyBotsSort.CLOSED_TRADES]: "number_of_closed_trades",
  [MyBotsSort.OPEN_TRADES]: "number_of_open_trades",
  [MyBotsSort.NEXT_ENTRY_OR_STOP]: [
    "active_trade.next_entry.distance",
    "active_trade.next_stop.distance",
  ],
  [MyBotsSort.SYMBOL]: "symbols[0]",
  [MyBotsSort.GROUP]: "sub_group_name",
  [MyBotsSort.CREATED]: "created_at",
  [MyBotsSort.REALIZED_PNL]: "realised_pnl.USD",
  [MyBotsSort.TAKE_PROFITS_FILLED]: "take_profits_filled",
  [MyBotsSort.AMOUNT]: sortBotsByAmount,
  [MyBotsSort.LAST_TRADE_OPENED]: "last_trade",
};
const ABS_SORTS = [MyBotsSort.NEXT_TAKE_PROFIT, MyBotsSort.NEXT_ENTRY_OR_STOP];
const OPPOSITE_SORTS: MyBotsSort[] = [
  MyBotsSort.LAST_ENTRY,
  MyBotsSort.LAST_TRADE_OPENED,
];
const STRING_SORTS = [MyBotsSort.SYMBOL, MyBotsSort.GROUP, MyBotsSort.NAME];
